import { logger } from "../common/logger";

export const isEmptyObject = (obj: object) => {
  for (const value of Object.values(obj)) {
    if (value !== undefined) {
      return false;
    }
  }
  return true;
};

export function serialGroupBy<A, B>(
  values: readonly A[],
  keyOf: (a: A) => B,
  sameKey: (one: B, two: B) => boolean,
): [B, A[]][] {
  const groups: [B, A[]][] = [];
  let last: [B, A[]] | undefined;
  for (const value of values) {
    const key = keyOf(value);
    if (!last || !sameKey(last[0], key)) {
      last = [key, []];
      groups.push(last);
    }
    last[1].push(value);
  }
  return groups;
}

export function truth<A>(value: A | null | undefined): value is A {
  return value !== null && typeof value !== "undefined";
}

export function truthOrFail<A>(
  value: A | null | undefined,
  message = "Unexpected value",
): A {
  if (!truth(value)) {
    throw new Error(message);
  }
  return value;
}

export function assertNever(x: never): never {
  throw new Error(`Unexpected value: ${x}`);
}

export function logNever(x: never): void {
  logger.error("%s unexpected value here", x);
}

export function intersperse<A>(a: (index: number) => A, xs: A[]): A[] {
  const result = [];
  for (const x of xs) {
    if (result.length > 0) {
      result.push(a(result.length));
    }
    result.push(x);
  }
  return result;
}

export function addIfDefined<T>(
  key: string,
  value: T | undefined,
): { [key: string]: T } | object {
  return value ? { [key]: value } : {};
}

export const supportsPassiveEvents = (): boolean => {
  try {
    const opts = Object.defineProperty({}, "passive", {
      get: () => true,
    });
    window.addEventListener("testPassive", () => {}, opts);
    window.removeEventListener("testPassive", () => {}, opts);
    return true;
  } catch {
    return false;
  }
};

export const sleep = (millis: number, signal?: AbortSignal | undefined) =>
  new Promise<void>((resolve, reject) => {
    // eslint-disable-next-line prefer-const
    let timeout: ReturnType<typeof setTimeout>;
    signal?.addEventListener("abort", () => {
      clearTimeout(timeout);
      reject("Aborted");
    });
    timeout = setTimeout(resolve, millis);
  });

export function keepAll<X, Y extends X>(
  xs: X[],
  p: (x: X) => x is Y,
): asserts xs is Y[] {
  let index = 0;
  while (index < xs.length) {
    if (p(xs[index]!)) {
      index += 1;
    } else {
      xs.splice(index, 1);
    }
  }
}

export function invariant<Truth extends boolean>(
  truth: Truth,
  message = "Invariant failed",
): asserts truth {
  if (!truth) {
    throw new Error(message);
  }
}

export function assumeNullish<T>(x: T): T | null | undefined {
  return x;
}

export function mapReverse<X, Y>(xs: readonly X[], f: (x: X) => Y): Y[] {
  const ys = [];
  for (let i = xs.length - 1; i >= 0; i--) {
    ys.push(f(xs[i]));
  }
  return ys;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
